import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  createTournament,
  deleteTournament,
  listTournaments,
  updateTournament,
  type Tournament,
} from "@/libs/tournamentApi";

import {
  TournamentFormDialog,
  type TournamentFormData,
} from "./tournament-form-dialog";

type StatusFilter = "all" | "draft" | "open" | "published";
type DateSort = "latest" | "oldest";

interface FacilityScope {
  facilityId: string | null;
  generation: number;
}

interface ScopedMessage {
  scope: FacilityScope;
  message: string;
}

type DialogState =
  | { mode: "closed" }
  | {
      mode: "edit" | "clone";
      scope: FacilityScope;
      session: number;
      tournament: Tournament;
    };

interface PendingDelete {
  scope: FacilityScope;
  session: number;
  tournament: Tournament;
}

type RefreshResult =
  | { status: "applied" }
  | { status: "stale" }
  | { status: "error"; error: unknown };

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "open", label: "Open" },
  { value: "published", label: "Published" },
];

const PAGE_SIZE = 10;

function sameScope(
  left: FacilityScope | null | undefined,
  right: FacilityScope | null | undefined,
) {
  return (
    Boolean(left) &&
    Boolean(right) &&
    left?.facilityId === right?.facilityId &&
    left?.generation === right?.generation
  );
}

function messageForScope(value: ScopedMessage | null, scope: FacilityScope) {
  return value && sameScope(value.scope, scope) ? value.message : null;
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function formatDate(iso: string | null | undefined) {
  return iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";
}

function humanize(value: string) {
  return value.replaceAll("_", " ");
}

function statusColor(
  status: string,
): "default" | "error" | "info" | "success" | "warning" {
  if (status === "cancelled") return "error";
  if (status === "registration_open" || status === "open") return "success";
  if (status === "in_progress") return "info";
  if (status === "draft") return "warning";
  return "default";
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Box sx={{ minWidth: 92 }}>
      <Typography
        component="div"
        sx={{ fontWeight: 700, lineHeight: 1.2 }}
        variant="subtitle1"
      >
        {value}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
    </Box>
  );
}

export function TournamentsWorkspace() {
  const navigate = useNavigate();
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const facilityScopeRef = useRef<FacilityScope>({
    facilityId: null,
    generation: 0,
  });
  if (facilityScopeRef.current.facilityId !== facilityId) {
    facilityScopeRef.current = {
      facilityId,
      generation: facilityScopeRef.current.generation + 1,
    };
  }
  const renderedScope = facilityScopeRef.current;

  const [rows, setRows] = useState<Tournament[]>([]);
  const rowsScopeRef = useRef<FacilityScope | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ScopedMessage | null>(null);
  const [warning, setWarning] = useState<ScopedMessage | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const dialogSessionRef = useRef(0);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );
  const deleteSessionRef = useRef(0);
  const deleteInFlightRef = useRef<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const listRequestRef = useRef(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateSort, setDateSort] = useState<DateSort>("latest");
  const [page, setPage] = useState(1);

  const isCurrentScope = useCallback(
    (scope: FacilityScope) => sameScope(facilityScopeRef.current, scope),
    [],
  );

  const refresh = useCallback(
    async (scope: FacilityScope): Promise<RefreshResult> => {
      if (!scope.facilityId) return { status: "stale" };

      const request = ++listRequestRef.current;
      try {
        const nextRows = await listTournaments(scope.facilityId);
        if (!isCurrentScope(scope) || request !== listRequestRef.current) {
          return { status: "stale" };
        }
        rowsScopeRef.current = scope;
        setRows(nextRows);
        return { status: "applied" };
      } catch (cause) {
        if (!isCurrentScope(scope) || request !== listRequestRef.current) {
          return { status: "stale" };
        }
        return { status: "error", error: cause };
      }
    },
    [isCurrentScope],
  );

  useEffect(() => {
    const scope = renderedScope;
    listRequestRef.current += 1;
    deleteSessionRef.current += 1;
    deleteInFlightRef.current = null;
    rowsScopeRef.current = scope;
    setRows([]);
    setError(null);
    setWarning(null);
    setDialog({ mode: "closed" });
    setPendingDelete(null);
    setDeleting(false);
    setPage(1);

    if (!scope.facilityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    void (async () => {
      const result = await refresh(scope);
      if (!isCurrentScope(scope)) return;
      if (result.status === "error") {
        setError({
          scope,
          message: errorMessage(result.error, "Failed to load tournaments"),
        });
      }
      setLoading(false);
    })();
  }, [isCurrentScope, refresh, renderedScope]);

  const visibleRows = sameScope(rowsScopeRef.current, renderedScope)
    ? rows
    : [];
  const inventoryLoading =
    facilityLoading ||
    !facilityId ||
    loading ||
    !sameScope(rowsScopeRef.current, renderedScope);
  const visibleError = messageForScope(error, renderedScope);
  const visibleWarning = messageForScope(warning, renderedScope);
  const metricsUnavailable = inventoryLoading || Boolean(visibleError);
  const activeDialog =
    dialog.mode !== "closed" && sameScope(dialog.scope, renderedScope)
      ? dialog
      : ({ mode: "closed" } as const);
  const formTournament = useMemo(() => {
    if (activeDialog.mode === "edit") return activeDialog.tournament;
    if (activeDialog.mode === "clone") {
      return {
        ...activeDialog.tournament,
        name: `${activeDialog.tournament.name} (copy)`,
      };
    }
    return null;
  }, [activeDialog]);
  const activeDelete =
    pendingDelete && sameScope(pendingDelete.scope, renderedScope)
      ? pendingDelete
      : null;

  const published = visibleRows.filter(
    (tournament) => tournament.is_published,
  ).length;
  const registrationOpen = visibleRows.filter(
    (tournament) =>
      (tournament.effective_status ?? tournament.status) ===
      "registration_open",
  ).length;
  const drafts = visibleRows.filter(
    (tournament) => tournament.status === "draft",
  ).length;

  const filteredSortedRows = useMemo(() => {
    const filtered = visibleRows.filter((tournament) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "published") return tournament.is_published;
      if (statusFilter === "open") {
        return (
          (tournament.effective_status ?? tournament.status) ===
          "registration_open"
        );
      }
      return tournament.status === statusFilter;
    });

    return [...filtered].sort((left, right) => {
      if (!left.start_date && !right.start_date) return 0;
      if (!left.start_date) return 1;
      if (!right.start_date) return -1;

      const leftTime = new Date(left.start_date).getTime();
      const rightTime = new Date(right.start_date).getTime();
      return dateSort === "latest"
        ? rightTime - leftTime
        : leftTime - rightTime;
    });
  }, [dateSort, statusFilter, visibleRows]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredSortedRows.length / PAGE_SIZE),
  );
  const paginatedRows = filteredSortedRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );
  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, pageCount));
  }, [pageCount]);

  const openTournament = (tournamentId: string) => {
    navigate(`/tournaments/${tournamentId}`);
  };

  // #2691: new tournaments are set up on one page (/tournaments/new); the
  // dialog only edits or clones (Clone moves to "Copy a past tournament", PR E).
  const openNewTournament = () => {
    if (!renderedScope.facilityId) return;
    navigate("/tournaments/new");
  };

  const openTournamentDialog = (
    mode: "edit" | "clone",
    tournament: Tournament,
  ) => {
    dialogSessionRef.current += 1;
    setDialog({
      mode,
      scope: renderedScope,
      session: dialogSessionRef.current,
      tournament,
    });
  };

  const closeDialogSession = (session: number) => {
    setDialog((current) =>
      current.mode !== "closed" && current.session === session
        ? { mode: "closed" }
        : current,
    );
  };

  const submitTournament = async (data: TournamentFormData) => {
    if (activeDialog.mode === "closed" || !activeDialog.scope.facilityId) {
      return;
    }

    const snapshot = activeDialog;
    const scope = snapshot.scope;
    const scopeFacilityId = scope.facilityId;
    if (!scopeFacilityId) return;
    setError(null);
    setWarning(null);

    if (snapshot.mode === "edit") {
      let updated: Tournament;
      try {
        updated = await updateTournament(
          snapshot.tournament.tournament_id,
          data,
        );
      } catch (cause) {
        if (isCurrentScope(scope)) throw cause;
        return;
      }

      if (!isCurrentScope(scope)) return;
      if (sameScope(rowsScopeRef.current, scope)) {
        setRows((currentRows) =>
          currentRows.map((tournament) =>
            tournament.tournament_id === updated.tournament_id
              ? updated
              : tournament,
          ),
        );
      }
      closeDialogSession(snapshot.session);

      const result = await refresh(scope);
      if (!isCurrentScope(scope) || result.status !== "error") return;
      setWarning({
        scope,
        message:
          "Changes were saved, but the tournament list could not be refreshed. The saved tournament is shown below.",
      });
      return;
    }

    let created: Tournament;
    try {
      created = await createTournament(scopeFacilityId, {
        ...data,
        is_published: false,
      });
    } catch (cause) {
      if (isCurrentScope(scope)) throw cause;
      return;
    }

    if (!isCurrentScope(scope)) return;
    closeDialogSession(snapshot.session);
    navigate(`/tournaments/${created.tournament_id}`);
  };

  const requestDelete = (tournament: Tournament) => {
    deleteSessionRef.current += 1;
    setPendingDelete({
      scope: renderedScope,
      session: deleteSessionRef.current,
      tournament,
    });
  };

  const closeDeleteDialog = () => {
    if (deleteInFlightRef.current !== null) return;
    setPendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!activeDelete || deleteInFlightRef.current !== null) return;

    const snapshot = activeDelete;
    const request = snapshot.session;
    deleteInFlightRef.current = request;
    setDeleting(true);
    setError(null);
    setWarning(null);

    try {
      await deleteTournament(snapshot.tournament.tournament_id);
    } catch (cause) {
      if (
        isCurrentScope(snapshot.scope) &&
        deleteInFlightRef.current === request
      ) {
        setError({
          scope: snapshot.scope,
          message: errorMessage(cause, "Delete failed"),
        });
        deleteInFlightRef.current = null;
        setDeleting(false);
      }
      return;
    }

    if (
      !isCurrentScope(snapshot.scope) ||
      deleteInFlightRef.current !== request
    ) {
      return;
    }

    setPendingDelete((current) =>
      current?.session === request ? null : current,
    );
    if (sameScope(rowsScopeRef.current, snapshot.scope)) {
      setRows((currentRows) =>
        currentRows.filter(
          (tournament) =>
            tournament.tournament_id !== snapshot.tournament.tournament_id,
        ),
      );
    }
    deleteInFlightRef.current = null;
    setDeleting(false);

    const result = await refresh(snapshot.scope);
    if (!isCurrentScope(snapshot.scope) || result.status !== "error") return;
    setWarning({
      scope: snapshot.scope,
      message:
        "The tournament was deleted, but the tournament list could not be refreshed. The deleted tournament remains hidden.",
    });
  };

  if (facilityLoading || !facilityId) {
    return (
      <Box
        role="status"
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: "40vh",
          px: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary" variant="body2">
            Loading tournaments…
          </Typography>
        </Stack>
      </Box>
    );
  }

  const formOpen = activeDialog.mode !== "closed";

  return (
    <Stack spacing={2} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
            Tournaments
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {facility?.name ?? "Manage tournaments for this facility."}
          </Typography>
        </Box>
        <Button
          disabled={inventoryLoading}
          onClick={openNewTournament}
          size="small"
          startIcon={<AddRoundedIcon />}
          variant="contained"
        >
          New tournament
        </Button>
      </Stack>

      {visibleError ? <Alert severity="error">{visibleError}</Alert> : null}
      {visibleWarning ? (
        <Alert severity="warning">{visibleWarning}</Alert>
      ) : null}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                component="h2"
                sx={{ fontWeight: 700 }}
                variant="subtitle1"
              >
                Tournament inventory
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Review schedules, publishing state, and registration readiness.
              </Typography>
            </Box>
            <Stack
              direction="row"
              sx={{ flexWrap: "wrap", gap: { xs: 1.5, sm: 3 } }}
              useFlexGap
            >
              <Metric
                label="Total"
                value={metricsUnavailable ? "—" : visibleRows.length}
              />
              <Metric
                label="Published"
                value={metricsUnavailable ? "—" : published}
              />
              <Metric
                label="Registration open"
                value={metricsUnavailable ? "—" : registrationOpen}
              />
              <Metric label="Draft" value={metricsUnavailable ? "—" : drafts} />
            </Stack>
          </Stack>

          <Divider />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="tournament-status-filter-label">
                Status
              </InputLabel>
              <Select
                label="Status"
                labelId="tournament-status-filter-label"
                onChange={(event) => {
                  setStatusFilter(event.target.value as StatusFilter);
                  setPage(1);
                }}
                value={statusFilter}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="tournament-date-order-label">
                Date order
              </InputLabel>
              <Select
                label="Date order"
                labelId="tournament-date-order-label"
                onChange={(event) => {
                  setDateSort(event.target.value as DateSort);
                  setPage(1);
                }}
                value={dateSort}
              >
                <MenuItem value="latest">Latest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Divider />

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Tournaments" size="small" sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tournament</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Pricing</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventoryLoading ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 5 }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ alignItems: "center", justifyContent: "center" }}
                    >
                      <CircularProgress size={18} />
                      <Typography color="text.secondary" variant="body2">
                        Loading tournaments…
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : visibleError && visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 5 }}>
                    <Typography color="text.secondary" variant="body2">
                      Tournament list unavailable.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 5 }}>
                    <Typography color="text.secondary" variant="body2">
                      No tournaments yet. Create your first one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredSortedRows.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 5 }}>
                    <Typography color="text.secondary" variant="body2">
                      No tournaments match this filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((tournament) => {
                  const effectiveStatus =
                    tournament.effective_status ?? tournament.status;
                  return (
                    <TableRow
                      hover
                      key={tournament.tournament_id}
                      onClick={() => openTournament(tournament.tournament_id)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openTournament(tournament.tournament_id);
                        }
                      }}
                      tabIndex={0}
                      sx={{
                        cursor: "pointer",
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <TableCell sx={{ maxWidth: 320, whiteSpace: "normal" }}>
                        <Typography sx={{ fontWeight: 600 }} variant="body2">
                          {tournament.name}
                        </Typography>
                        {tournament.description ? (
                          <Typography
                            color="text.secondary"
                            noWrap
                            variant="caption"
                          >
                            {tournament.description}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {humanize(tournament.tournament_type)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          color={statusColor(effectiveStatus)}
                          label={humanize(effectiveStatus)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(tournament.start_date)} –{" "}
                        {formatDate(tournament.end_date)}
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {humanize(tournament.pricing_model)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.25}
                          sx={{ justifyContent: "flex-end" }}
                        >
                          <Tooltip title="Open">
                            <IconButton
                              aria-label={`Open ${tournament.name}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openTournament(tournament.tournament_id);
                              }}
                              size="small"
                            >
                              <OpenInNewRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label={`Edit ${tournament.name}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openTournamentDialog("edit", tournament);
                              }}
                              size="small"
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Clone">
                            <IconButton
                              aria-label={`Clone ${tournament.name}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openTournamentDialog("clone", tournament);
                              }}
                              size="small"
                            >
                              <ContentCopyRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              aria-label={`Delete ${tournament.name}`}
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                requestDelete(tournament);
                              }}
                              size="small"
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!inventoryLoading && visibleRows.length > 0 ? (
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: "center",
              borderTop: 1,
              borderColor: "divider",
              justifyContent: "flex-end",
              px: 2,
              py: 1.25,
            }}
          >
            <Button
              disabled={page === 1}
              onClick={() =>
                setPage((currentPage) => Math.max(1, currentPage - 1))
              }
              size="small"
              variant="outlined"
            >
              Prev
            </Button>
            <Typography color="text.secondary" variant="body2">
              Page {page} of {pageCount}
            </Typography>
            <Button
              disabled={page === pageCount}
              onClick={() =>
                setPage((currentPage) => Math.min(pageCount, currentPage + 1))
              }
              size="small"
              variant="outlined"
            >
              Next
            </Button>
          </Stack>
        ) : null}
      </Paper>

      <TournamentFormDialog
        key={
          activeDialog.mode === "closed"
            ? `closed-${renderedScope.generation}`
            : `${activeDialog.scope.generation}-${activeDialog.session}`
        }
        onClose={() => {
          if (activeDialog.mode !== "closed") {
            closeDialogSession(activeDialog.session);
          }
        }}
        mode={activeDialog.mode === "closed" ? "edit" : activeDialog.mode}
        onSubmit={submitTournament}
        open={formOpen}
        submitLabel={activeDialog.mode === "edit" ? "Save changes" : "Create"}
        title={
          activeDialog.mode === "edit"
            ? "Edit tournament"
            : activeDialog.mode === "clone"
              ? "Clone tournament"
              : "New tournament"
        }
        tournament={formTournament}
      />

      <Dialog
        aria-describedby="delete-tournament-description"
        aria-labelledby="delete-tournament-title"
        fullWidth
        maxWidth="xs"
        onClose={closeDeleteDialog}
        open={Boolean(activeDelete)}
      >
        <DialogTitle id="delete-tournament-title">
          Delete tournament?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-tournament-description">
            This permanently deletes{" "}
            <strong>{activeDelete?.tournament.name}</strong>. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleting}
            onClick={closeDeleteDialog}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleting}
            onClick={confirmDelete}
            size="small"
            variant="contained"
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
